import { Flex, Space } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { AppButton, AppLoader, AppText, AppTitle, SettingsSectionTitle } from "@/shared/Components";
import { getSafeLocalizedValue, priceFormat } from "@/shared/utils";
import { WarningModal } from "@/features/modals/WarningModal";
import { CurrencyCode, SubscriptionState } from "@/services/api/entities/payment";
import { ModalWrapper } from "@/features/modals/ModalWrapper";
import { useSubscriptionBlock } from "../model";
import styles from "./styles.module.scss";
import { HistoryContent } from "./components";

export const SubscriptionBlock = () => {
  const { t } = useTranslation();
  const { state, methods } = useSubscriptionBlock();

  const {
    subscription,
    isLoading,
    isModalOpen,
    cancelSubscriptionIsLoading,
    disabledUnsubscribe,
    isHistoryModalOpen,
  } = state;
  const {
    hideModal,
    handleClickUnsubscribe,
    handleCancelSubscription,
    handleClickRenew,
    showHistoryModal,
    hideHistoryModal,
  } = methods;

  if (isLoading) {
    return <AppLoader />;
  }

  return (
    <Flex className={styles.root} vertical gap={12}>
      <SettingsSectionTitle
        title={t("settingsPage.general.subscription.title")}
        button={
          <AppButton type="text" onClick={showHistoryModal} className={styles["history-button"]}>
            {t("settingsPage.general.subscription.historyButton")}
          </AppButton>
        }
      />
      {disabledUnsubscribe && (
        <p className={styles.hint}>{t("settingsPage.general.subscription.unsubscribeHint")}</p>
      )}
      <Flex justify="space-between" align="center">
        <AppText weight="semibold" size="lg">
          {getSafeLocalizedValue(subscription?.localizedTariffName, "OnPrem")}
        </AppText>
        <AppText weight="semibold" size="lg">
          {priceFormat({
            amount: subscription?.amountPaid.toString() ?? "",
            currencyCode: subscription?.currencyCode ?? CurrencyCode.RUR,
          })}
        </AppText>
      </Flex>
      <Flex vertical>
        {subscription?.options.map((option) => (
          <Space key={option.localizedOptionName.ru} direction="vertical" style={{ rowGap: 0 }}>
            <AppText weight="semibold" key={option.localizedOptionName.ru}>
              {getSafeLocalizedValue(option.localizedOptionName, option.localizedOptionName.ru)}
            </AppText>
            <AppText>
              {getSafeLocalizedValue(
                option.localizedOptionDescription,
                option.localizedOptionDescription.ru,
              )}
            </AppText>
          </Space>
        ))}
      </Flex>
      <Flex justify="space-between" align="center">
        <AppText className={styles["active-till"]}>
          {t(
            `settingsPage.general.subscription.${subscription?.subscriptionState === SubscriptionState.ACTIVE ? "active" : "readOnly"}`,
          )}
        </AppText>
        <AppText className={styles["active-till"]}>
          {dayjs(subscription?.activeEndDate).format("DD.MM.YYYY")}
        </AppText>
      </Flex>
      <Flex justify="space-between" className={styles.buttonsWrapper} gap={12}>
        <AppButton type="primary" onClick={handleClickRenew}>
          {t("settingsPage.general.subscription.renew")}
        </AppButton>
        <AppButton
          onClick={handleClickUnsubscribe}
          loading={cancelSubscriptionIsLoading}
          disabled={disabledUnsubscribe}
          isOutlined
        >
          {t("settingsPage.general.subscription.unsubscribe")}
        </AppButton>
      </Flex>
      <WarningModal
        open={isModalOpen}
        onCancel={hideModal}
        onOk={handleCancelSubscription}
        message={t("settingsPage.general.subscription.unsubscribeMessage")}
        extraMessage={t("settingsPage.general.subscription.unsubscribeMessageExtra")}
        okButtonText={t("settingsPage.general.subscription.unsubscribeButton")}
        cancelButtonText={t("common.cancel")}
      />
      <ModalWrapper
        modalTitle={
          <AppTitle level={4} className={styles["history-title"]}>
            {t("settingsPage.general.subscription.historyTitle")}
          </AppTitle>
        }
        open={isHistoryModalOpen}
        onCancel={hideHistoryModal}
        okButtonProps={{ style: { display: "none" } }}
        cancelButtonProps={{ style: { display: "none" } }}
        width={320}
      >
        <HistoryContent onClose={hideHistoryModal} />
      </ModalWrapper>
    </Flex>
  );
};
